import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { takeUntil } from 'rxjs/operators';
import { ProjectsService } from '../../../../../../modules/projects/core/projects.service';

@Component({
    selector: 'app-multiple-time-select',
    templateUrl: './multiple-time-select.component.html',
    styleUrls: ['./multiple-time-select.component.scss', '../../../styles.scss'],
    standalone: false
})
export class MultipleTimeSelectComponent implements OnInit, OnDestroy {
  @Input() hideSubScript = false;
  @Input() label = '';
  @Input() fromTimeLabel = 'From time';
  @Input() toTimeLabel = 'To time';
  @Input() placeholder = '';
  @Input() parentFormGroup: FormGroup = new FormGroup<any>({});
  @Input() controlName = '';
  @Input() suffixIcon = '';
  @Input() timePicker = false;
  @Input() locationMenu = false;
  @Input() error = '';
  @Output() suffixClicked: EventEmitter<any> = new EventEmitter();
  @Output() checkForErrors: EventEmitter<any> = new EventEmitter();

  fieldSubscription: Subscription | undefined;
  isRequired = false;
  isDisabled = false;
  menuOpen = false;
  errorMessage = '';
  buttonLabels = ['AM', 'PM'];

  overlayPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
  ];

  @ViewChild('trigger', { static: false }) trigger!: CdkOverlayOrigin;  // Define ViewChild for trigger
  private readonly onDestroy = new Subject<void>();

  constructor(
    private cdRef: ChangeDetectorRef,
    private projectsService: ProjectsService,
  ) {

  }

  ngOnInit() {
    const control = this.parentFormGroup?.controls[this.controlName];

    this.fieldSubscription = control?.valueChanges.subscribe(() => {
      if (this.parentFormGroup.controls[this.controlName].touched) {
        this.parentFormGroup.controls[this.controlName].markAsUntouched();
      }

      // Get errors on the form field
      const errors = this.parentFormGroup?.controls?.[this.controlName]?.errors;
      // If errors exist, handle it
      if (errors) {
        // Clear errors on the form field
        this.parentFormGroup.controls[this.controlName].setErrors(null);

        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);

        // Add errors again to the form field for showing messages in the future
        this.parentFormGroup.controls[this.controlName].setErrors(errors);
      } else {
        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);
      }
    });

    if (control?.validator) {
      const validator = control?.validator({} as AbstractControl);
      this.isRequired = !!(validator && validator['required']);
    }

    this.parentFormGroup?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.errorCheck();
      });
  }

  toggleOverlay() {
    event.stopPropagation();
    this.menuOpen = !this.menuOpen;
  }

  closeOverlay() {
    if (this.errorMessage) {
      return;
    }

    // tslint:disable-next-line:prefer-const
    let { fromTime, toTime, fromTimePeriod, toTimePeriod } = this.parentFormGroup.getRawValue();

    if (fromTime?.length === 3 && !fromTime.includes(':')) {
      const hour = fromTime.slice(0, 2).padStart(2, '0');
      let minute = Number(fromTime[2]) * 10;

      if (minute > 59) {
        minute = 59;
      }

      fromTime = `${hour}:${String(minute).padStart(2, '0')}`;
    }

    if (toTime?.length === 3 && !toTime.includes(':')) {
      const hour = toTime.slice(0, 2).padStart(2, '0');
      let minute = Number(toTime[2]) * 10;

      if (minute > 59) {
        minute = 59;
      }

      toTime = `${hour}:${String(minute).padStart(2, '0')}`;
    }

    const hasFromTime = fromTime && fromTimePeriod;
    const hasToTime = toTime && toTimePeriod;

    if (hasFromTime && hasToTime) {
      const combinedTime = `${this.formatTime(fromTime)} ${fromTimePeriod.toLowerCase()} - ${this.formatTime(toTime)} ${toTimePeriod.toLowerCase()}`;
      this.parentFormGroup.controls['time'].setValue(combinedTime);
      this.parentFormGroup.controls['fromTime'].setValue(fromTime);
      this.parentFormGroup.controls['toTime'].setValue(toTime);
    } else if (hasFromTime) {
      const combinedTime = `${this.formatTime(fromTime)} ${fromTimePeriod.toLowerCase()}`;
      this.parentFormGroup.controls['time'].setValue(combinedTime);
      this.parentFormGroup.controls['fromTime'].setValue(fromTime);
    } else {
      this.parentFormGroup.controls['time'].setValue(null);
      this.parentFormGroup.controls['fromTime'].setValue(null);
      this.parentFormGroup.controls['toTime'].setValue(null);
      this.parentFormGroup.controls['fromTimePeriod'].setValue(null);
      this.parentFormGroup.controls['toTimePeriod'].setValue(null);
      this.projectsService.isAnyFormDirty.next(false);
    }

    this.menuOpen = false;
  }

  errorCheck() {
    const fromTime = this.parentFormGroup.controls['fromTime']?.value;
    const toTime = this.parentFormGroup.controls['toTime']?.value;
    const fromTimePeriod = this.parentFormGroup.controls['fromTimePeriod']?.value;
    const toTimePeriod = this.parentFormGroup.controls['toTimePeriod']?.value;

    if (fromTime && toTime && fromTimePeriod && toTimePeriod) {
      const fromDateTime = this.convertTo24Hour(fromTime, fromTimePeriod);
      const toDateTime = this.convertTo24Hour(toTime, toTimePeriod);

      if (fromDateTime > toDateTime) {
        this.errorMessage = `${this.fromTimeLabel} cannot be later than ${this.toTimeLabel}`;
      } else if (fromDateTime === toDateTime) {
        this.errorMessage = `${this.fromTimeLabel} cannot be the same as ${this.toTimeLabel}`;
      } else {
        this.errorMessage = '';
      }
    } else {
      this.errorMessage = '';
    }
  }

  formatTime(timeStr: string): string {
    const formattedTime = timeStr.startsWith('0') ? timeStr.substring(1) : timeStr;

    if (formattedTime.endsWith(':00')) {
      return formattedTime.split(':')[0];
    }

    return formattedTime;
  }

  ngOnDestroy() {
    this.fieldSubscription?.unsubscribe();
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  private convertTo24Hour(time: string, period: string): number {
    if (!time || !period) {
      return 0;
    }

    // Default to "00" minutes if only hours are given
    if (!time.includes(':')) {
      time += ':00';
    }

    const [hoursStr, minutesStr] = time.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    if (isNaN(hours) || isNaN(minutes)) {
      return 0;
    }

    let hour24 = hours;

    if (period.toUpperCase() === 'PM' && hours !== 12) {
      hour24 += 12;
    } else if (period.toUpperCase() === 'AM' && hours === 12) {
      hour24 = 0;
    }

    return hour24 * 60 + minutes;
  }
}
